import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface WorkloadMember {
  id: string;
  name: string;
  avatar: string;
  currentMandates: number;
  maxMandates: number;
  status: 'online' | 'busy' | 'offline';
}

export const WorkloadDistribution = () => {
  const { data: workloadMembers = [], isLoading } = useQuery({
    queryKey: ['workload-distribution'],
    queryFn: async () => {
      const { data: users, error } = await supabase
        .from('users')
        .select('id, first_name, last_name');

      if (error) throw error;

      if (!users || users.length === 0) return [];

      const membersWithWorkload = await Promise.all(users.map(async (user) => {
        const { count } = await supabase
          .from('mandate_team_members')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id);

        const currentMandates = count || 0;
        const maxMandates = 10; // Default capacity
        const status: 'online' | 'busy' | 'offline' =
          currentMandates > maxMandates ? 'busy' :
          currentMandates > 0 ? 'online' : 'offline';

        return {
          id: user.id,
          name: `${user.first_name} ${user.last_name}`,
          avatar: '',
          currentMandates,
          maxMandates,
          status
        };
      }));

      return membersWithWorkload;
    }
  });
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return 'bg-success';
      case 'busy': return 'bg-brand-danger';
      case 'offline': return 'bg-gray-400';
      default: return 'bg-gray-400';
    }
  };

  const getWorkloadColor = (current: number, max: number) => {
    const percentage = (current / max) * 100;
    if (percentage > 100) return 'bg-brand-danger';
    if (percentage >= 80) return 'bg-brand-primary';
    if (percentage >= 60) return 'bg-warning';
    return 'bg-brand-primary';
  };

  const getWorkloadPercentage = (current: number, max: number) => {
    return Math.min((current / max) * 100, 100);
  };

  if (isLoading) {
    return (
      <div className="bg-white p-6 rounded-2xl border border-brand-border">
        <div className="text-center py-8 text-brand-text-secondary">Loading workload...</div>
      </div>
    );
  }

  if (workloadMembers.length === 0) {
    return (
      <div className="bg-white p-6 rounded-2xl border border-brand-border">
        <div className="text-center py-8 text-brand-text-secondary">No team members found</div>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-2xl border border-brand-border">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-bold text-brand-primary">Workload</h3>
        <button className="p-1.5 text-brand-text-secondary">
          <i className="fa-solid fa-ellipsis"></i>
        </button>
      </div>

      <div className="space-y-4">
        {workloadMembers.map((member) => (
          <div key={member.id} className="flex items-center gap-3">
            <div className="relative">
              <div className="w-10 h-10 rounded-full bg-brand-primary text-white flex items-center justify-center font-bold">
                {member.name.split(' ').map(n => n[0]).join('')}
              </div>
              <span className={`absolute bottom-0 right-0 w-3 h-3 ${getStatusColor(member.status)} border-2 border-white rounded-full`}></span>
            </div>
            
            <div className="flex-1">
              <div className="flex justify-between items-center mb-1">
                <p className="text-sm font-medium">{member.name}</p>
                <p className="text-xs text-brand-text-secondary">
                  {member.currentMandates}/{member.maxMandates}
                </p>
              </div>
              <div className="w-full bg-brand-secondary rounded-full h-2">
                <div 
                  className={`h-2 rounded-full ${getWorkloadColor(member.currentMandates, member.maxMandates)}`}
                  style={{ width: `${getWorkloadPercentage(member.currentMandates, member.maxMandates)}%` }}
                ></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};